/**
 * Hand a GET the shell started early to the view that asks for it.
 *
 * When an app opens, the shell requests the app's first-screen data at once,
 * while the app's code is still loading. The app's own first identical GET
 * then receives that response instead of starting a second request. A
 * response is handed over once, within a few seconds; a failed prefetch makes
 * the view request again, and a write drops every pending prefetch so no view
 * reads data from before it. A provider pull is a write that says whether it
 * changed anything, and drops them only when it did.
 */
import axios, {
  AxiosHeaders,
  CanceledError,
  type AxiosAdapter,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

const HANDOFF_WINDOW_MS = 5_000;
// Telemetry posts change no data a view shows.
const SIDE_CHANNEL_WRITES = ['/cortex/replays/', '/support/events', '/performance/'];
// Provider pulls answer `changed: false` when they wrote nothing. Calendar
// sends one as it mounts, while its own prefetched GETs are still pending, so
// clearing before every pull dropped them all. A pull that changed something,
// an answer without the flag (an older backend) and a failure still drop
// them once the pull is done, and Calendar refetches after such a pull.
const REFRESH_WRITES = ['/calendar-sync/sync-range'];

/** Whether a refresh write's answer says it changed nothing. */
function reportsNoChange(data: unknown): boolean {
  let body = data;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return false;
    }
  }
  return typeof body === 'object' && body !== null && 'changed' in body && body.changed === false;
}

interface PendingHandoff {
  readonly response: Promise<AxiosResponse>;
  readonly expiry: ReturnType<typeof setTimeout>;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Share an identical GET only while its network request is pending. */
    sharePendingGet?: boolean;
    /** Read consumer-specific authorization directly instead of adopting a prefetch. */
    handoffBypass?: boolean;
    /** Set by prefetchGet: hold the response for the view's own request. */
    handoffPrefetch?: boolean;
    /** Revision before the prefetch waits for authentication. */
    handoffPrefetchRevision?: number;
    /** Mutation revision when the adapter sent this write. */
    handoffWriteRevision?: number;
  }
}

/** The request's URL with its query sorted, so `params` and a literal query match. */
export function handoffKey(client: AxiosInstance, config: AxiosRequestConfig): string {
  const url = new URL(client.getUri(config), 'http://handoff.invalid');
  url.searchParams.sort();
  const query = url.searchParams.toString();
  return query ? `${url.pathname}?${query}` : url.pathname;
}

/**
 * A `fetch` response as the client would have returned it: the parsed JSON
 * body, or a rejection for a status outside 2xx so the view requests again
 * through the client, which refreshes the session on a 401.
 */
export async function responseFromFetch(response: Promise<Response>): Promise<AxiosResponse> {
  const answer = await response;
  if (!answer.ok) throw new Error(`The early request answered ${answer.status}`);
  return {
    data: await answer.json(),
    status: answer.status,
    statusText: answer.statusText,
    headers: Object.fromEntries(answer.headers.entries()),
    config: { headers: new AxiosHeaders() },
  };
}

export interface GetHandoff {
  /** Start a GET now and hold its response for the next identical GET. */
  prefetch(path: string): void;
  /**
   * Start one GET whose answer carries what several other GETs return, and
   * hold each part for the next GET of its own path. A part the answer leaves
   * out, such as from an older backend, makes that GET request again. Nothing
   * is sent while any part is still held.
   */
  prefetchParts(
    path: string,
    parts: ReadonlyArray<{ path: string; select: (data: unknown) => unknown }>
  ): void;
  /**
   * Hold the response of a GET sent outside the client, such as the entry
   * script's before the client existed, for the next identical GET. A
   * rejected response makes that GET request again.
   */
  adopt(
    request: Pick<AxiosRequestConfig, 'url' | 'params'>,
    response: Promise<AxiosResponse>
  ): void;
  /** Whether a response waits for the next GET of this path. */
  isHeld(path: string): boolean;
  /** Whether no write or clear has superseded the source read. */
  isCurrentRead(response: AxiosResponse): boolean;
  /** Adopt a derived read only while its source read is still current. */
  adoptFromRead(
    request: Pick<AxiosRequestConfig, 'url' | 'params'>,
    response: AxiosResponse
  ): boolean;
  /** Adopt a derived read only if its source write has not been superseded. */
  adoptFromWrite(
    request: Pick<AxiosRequestConfig, 'url' | 'params'>,
    response: AxiosResponse
  ): boolean;
  /** Drop every pending prefetch. */
  clear(): void;
}

export function installGetHandoff(
  client: AxiosInstance,
  { onUnused = () => undefined }: { onUnused?: (key: string) => void } = {}
): GetHandoff {
  const pending = new Map<string, PendingHandoff>();
  const sharedReads = new Map<string, Promise<AxiosResponse>>();
  const readRevisions = new WeakMap<AxiosRequestConfig, number>();
  let writeRevision = 0;
  const send: AxiosAdapter = axios.getAdapter(client.defaults.adapter);

  const drop = (key: string) => {
    const handoff = pending.get(key);
    if (!handoff) return;
    clearTimeout(handoff.expiry);
    pending.delete(key);
  };
  const clear = () => {
    writeRevision++;
    sharedReads.clear();
    for (const key of [...pending.keys()]) drop(key);
  };
  const hold = (key: string, response: Promise<AxiosResponse>) => {
    const expiry = setTimeout(() => {
      if (pending.get(key)?.response !== response) return;
      pending.delete(key);
      onUnused(key);
    }, HANDOFF_WINDOW_MS);
    pending.set(key, { response, expiry });
  };

  const sendRead = async (
    config: InternalAxiosRequestConfig,
    key: string
  ): Promise<AxiosResponse> => {
    if (
      !config.sharePendingGet ||
      config.signal ||
      config.cancelToken ||
      config.auth ||
      config.data !== undefined ||
      config.onDownloadProgress ||
      config.onUploadProgress ||
      config.validateStatus !== client.defaults.validateStatus
    )
      return send(config);
    const headers = Object.entries(AxiosHeaders.from(config.headers).toJSON())
      .map(([name, value]) => [name.toLowerCase(), value])
      .sort(([left], [right]) => String(left).localeCompare(String(right)));
    const sharedKey = JSON.stringify([
      writeRevision,
      new URL(client.getUri(config), 'http://handoff.invalid').origin,
      key,
      config.responseType,
      config.timeout,
      config.timeoutErrorMessage,
      config.withCredentials,
      headers,
    ]);
    let response = sharedReads.get(sharedKey);
    if (!response) {
      response = send(config);
      sharedReads.set(sharedKey, response);
    }
    try {
      return { ...(await response), config };
    } finally {
      if (sharedReads.get(sharedKey) === response) sharedReads.delete(sharedKey);
    }
  };

  client.defaults.adapter = async config => {
    const method = (config.method ?? 'get').toLowerCase();
    if (method !== 'get') {
      const url = config.url ?? '';
      if (method === 'post' && /\/dashboards\/chart-documents\/?(?:\?|$)/.test(url)) {
        readRevisions.set(config, writeRevision);
        return send(config);
      }
      if (SIDE_CHANNEL_WRITES.some(prefix => url.includes(prefix))) return send(config);
      if (!REFRESH_WRITES.some(prefix => url.includes(prefix))) {
        clear();
        config.handoffWriteRevision = writeRevision;
        return send(config);
      }
      try {
        const response = await send(config);
        if (!reportsNoChange(response.data)) clear();
        return response;
      } catch (error) {
        clear();
        throw error;
      }
    }
    readRevisions.set(config, writeRevision);
    const key = handoffKey(client, config);
    if (config.handoffPrefetch) {
      if (config.handoffPrefetchRevision !== writeRevision) {
        throw new CanceledError('The prefetch was cleared before authentication resolved');
      }
      const started = pending.get(key);
      if (started) return started.response;
      const response = send(config);
      hold(key, response);
      return response;
    }
    if (config.handoffBypass) return sendRead(config, key);
    const handoff = pending.get(key);
    if (!handoff) return sendRead(config, key);
    drop(key);
    try {
      return { ...(await handoff.response), config };
    } catch {
      return sendRead(config, key);
    }
  };

  return {
    prefetch: path => {
      void client
        .get(path, { handoffPrefetch: true, handoffPrefetchRevision: writeRevision })
        .catch(() => undefined);
    },
    prefetchParts: (path, parts) => {
      const keyed = parts.map(part => ({
        ...part,
        key: handoffKey(client, { url: part.path, method: 'get' }),
      }));
      if (keyed.some(part => pending.has(part.key))) return;
      const revision = writeRevision;
      const answer = client.get(path);
      answer.catch(() => undefined);
      for (const part of keyed) {
        const response = answer.then(whole => {
          const data = part.select(whole.data);
          if (revision !== writeRevision || data === undefined || data === null) {
            throw new Error('The combined read did not answer this part');
          }
          return { ...whole, data };
        });
        response.catch(() => undefined);
        hold(part.key, response);
      }
    },
    adopt: (request, response) => {
      const key = handoffKey(client, { ...request, method: 'get' });
      response.catch(() => undefined);
      if (pending.has(key)) return;
      hold(key, response);
    },
    isHeld: path => pending.has(handoffKey(client, { url: path, method: 'get' })),
    isCurrentRead: response => readRevisions.get(response.config) === writeRevision,
    adoptFromRead: (request, response) => {
      if (readRevisions.get(response.config) !== writeRevision) return false;
      const key = handoffKey(client, { ...request, method: 'get' });
      if (pending.has(key)) return false;
      hold(key, Promise.resolve(response));
      return true;
    },
    adoptFromWrite: (request, response) => {
      if (response.config.handoffWriteRevision !== writeRevision) return false;
      const key = handoffKey(client, { ...request, method: 'get' });
      if (pending.has(key)) return false;
      hold(key, Promise.resolve(response));
      return true;
    },
    clear,
  };
}
